import React from 'react';
import { Avatar, Box, Typography } from '@mui/material';
import { Person } from '@mui/icons-material';
import { fetchProfileSummary } from './profileApi';
import { fetchRoleBadge, readCachedRoleBadge, roleAccentColor, SessionRoleBadge } from './roleBadge';

interface HeaderProfileButtonProps {
  username?: string;
  fullName?: string;
  onClick?: () => void;
  /** Открыта страница профиля */
  isSelected?: boolean;
}

/**
 * Профиль пользователя в шапке: аватар + имя (ФИО или логин) и полоска цвета роли под именем.
 * Общий для /admin и /dashboard. Данные берутся из localStorage и обновляются
 * по событиям profileUpdated / avatarUpdated / profileStatusChanged.
 * Цвет полоски - цвет бейджа роли из конструктора ролей, чтобы роль была видна на скриншотах.
 */
const HeaderProfileButton: React.FC<HeaderProfileButtonProps> = ({
  username: propsUsername,
  fullName: propsFullName,
  onClick,
  isSelected = false
}) => {
  const username = propsUsername || localStorage.getItem('username') || 'Пользователь';

  const [fullName, setFullName] = React.useState(propsFullName || localStorage.getItem('fullName') || '');
  const [avatarUrl, setAvatarUrl] = React.useState<string | null>(localStorage.getItem('userAvatar') || null);
  const [isIncomplete, setIsIncomplete] = React.useState(
    () => localStorage.getItem('profileIncomplete') === 'true'
  );
  const roleSlug = localStorage.getItem('role') || '';
  const [roleBadge, setRoleBadge] = React.useState<SessionRoleBadge | null>(() => readCachedRoleBadge(roleSlug));

  React.useEffect(() => {
    if (propsFullName) setFullName(propsFullName);
  }, [propsFullName]);

  React.useEffect(() => {
    if (!roleSlug) return;
    let cancelled = false;
    fetchRoleBadge(roleSlug)
      .then(result => { if (!cancelled && result) setRoleBadge(result); })
      .catch(() => { /* остается цвет из кэша */ });
    return () => { cancelled = true; };
  }, [roleSlug]);

  React.useEffect(() => {
    const handleStatus = () => setIsIncomplete(localStorage.getItem('profileIncomplete') === 'true');
    const handleName = (e: Event) => {
      const detail = (e as CustomEvent<{ fullName?: string }>).detail;
      setFullName(detail?.fullName !== undefined ? detail.fullName : localStorage.getItem('fullName') || '');
    };
    const handleAvatar = (e: Event) => {
      const detail = (e as CustomEvent<{ avatarUrl?: string | null }>).detail;
      setAvatarUrl(detail?.avatarUrl !== undefined ? detail.avatarUrl : localStorage.getItem('userAvatar') || null);
    };

    window.addEventListener('profileStatusChanged', handleStatus);
    window.addEventListener('profileUpdated', handleName);
    window.addEventListener('avatarUpdated', handleAvatar);
    window.addEventListener('storage', handleStatus);
    window.addEventListener('storage', handleName);
    window.addEventListener('storage', handleAvatar);

    if (username && username !== 'Пользователь') {
      fetchProfileSummary()
        .then(summary => {
          if (summary.fullName) setFullName(summary.fullName);
          setAvatarUrl(summary.avatarUrl);
        })
        .catch(() => { /* остаётся значение из localStorage */ });
    }

    return () => {
      window.removeEventListener('profileStatusChanged', handleStatus);
      window.removeEventListener('profileUpdated', handleName);
      window.removeEventListener('avatarUpdated', handleAvatar);
      window.removeEventListener('storage', handleStatus);
      window.removeEventListener('storage', handleName);
      window.removeEventListener('storage', handleAvatar);
    };
  }, [username]);

  const displayName = fullName.trim() !== '' ? fullName : username;
  const incompleteColor = '#f97316';
  const roleColor = roleBadge ? roleAccentColor(roleBadge.badge) : null;
  const title = [
    isIncomplete ? 'Профиль требует проверки' : 'Профиль',
    roleBadge ? `Роль: ${roleBadge.name}` : ''
  ].filter(Boolean).join(' · ');

  return (
    <Box
      role="button"
      tabIndex={0}
      title={title}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 0.75,
        py: 0.25,
        borderRadius: '8px',
        cursor: 'pointer',
        userSelect: 'none',
        bgcolor: isSelected ? '#f4f4f5' : 'transparent',
        transition: 'background-color 0.15s ease',
        '&:hover': { bgcolor: '#f4f4f5' },
        '&:focus-visible': { outline: '2px solid #18181b', outlineOffset: 1 }
      }}
    >
      <Avatar
        src={avatarUrl || undefined}
        sx={{
          width: 28,
          height: 28,
          bgcolor: avatarUrl ? 'transparent' : '#18181b',
          color: '#ffffff',
          outline: isIncomplete ? `2px solid ${incompleteColor}` : 'none',
          outlineOffset: 1
        }}
      >
        {!avatarUrl && <Person sx={{ fontSize: 18 }} />}
      </Avatar>
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, maxWidth: 220 }}>
        <Typography
          sx={{
            fontSize: '0.875rem',
            lineHeight: 1.3,
            fontWeight: isSelected ? 600 : 500,
            color: isIncomplete ? '#ea580c' : '#18181b',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {displayName}
        </Typography>
        {roleColor && (
          <Box
            aria-label={roleBadge ? `Роль: ${roleBadge.name}` : undefined}
            sx={{ mt: '3px', height: 3, borderRadius: '2px', bgcolor: roleColor }}
          />
        )}
      </Box>
    </Box>
  );
};

export default HeaderProfileButton;
